/**
 * The online session — a WebSocket-backed implementation of GameBackend plus
 * lobby state. The server is authoritative: `dispatch` sends the action and the
 * authoritative (redacted) state comes back over the socket, which we reconcile
 * into a reactive store. Legal-move highlighting still runs locally because it
 * only needs the public board, not anyone's hidden cards.
 */
import { createStore, reconcile } from "solid-js/store";
import { createSignal } from "solid-js";
import type { Action, CardTypeId, DeckBuild, DraftState, GameState, PlayerId } from "../core";
import type { ClientMessage, ClockSnapshot, RoomMode, RoomPhase, Seats, ServerMessage } from "../shared/protocol";
import type { ChessClock } from "./clock";
import type { DraftBackend } from "./draftRunner";
import type { GameBackend } from "./store";
import { refreshAccount } from "./account";

export interface RatingChange {
  readonly delta: number;
  readonly rating: number;
}

export type OnlineStatus = "connecting" | RoomPhase | "disconnected";

export interface OnlineSession extends GameBackend {
  status(): OnlineStatus;
  seats(): Seats | null;
  mySeat(): PlayerId | null;
  /** The room's build mode (draft or classic), once known. */
  mode(): RoomMode | null;
  submitDeck(name: string, build: DeckBuild): void;
  /** Host-only: change how decks are built (draft or classic) while in the lobby. */
  setMode(mode: RoomMode): void;
  rematch(): void;
  /** The viewer's rating change from the last finished ranked game, if any. */
  rated(): RatingChange | null;
  /** The server's authoritative clock, or null for a clockless (legacy) game. */
  clock(): ChessClock | null;
  /** A DraftBackend over the server-driven draft, or null when not drafting. */
  draftBackend(): DraftBackend | null;
  close(): void;
}

interface Internal {
  game: GameState | null;
  status: OnlineStatus;
  seats: Seats | null;
  mySeat: PlayerId | null;
  mode: RoomMode | null;
  draft: DraftState | null;
  error: string | null;
  rated: RatingChange | null;
  clock: ClockSnapshot | null;
}

/** Pass a null token to connect as a read-only spectator. */
export function createOnlineSession(roomId: string, token: string | null): OnlineSession {
  const [s, set] = createStore<Internal>({
    game: null,
    status: "connecting",
    seats: null,
    mySeat: null,
    mode: null,
    draft: null,
    error: null,
    rated: null,
    clock: null,
  });

  // A DraftBackend over the server-authoritative draft: state comes from the
  // store; picks/passes are sent to the server, which validates and rebroadcasts.
  const draftBackend: DraftBackend = {
    get state() {
      return s.draft as DraftState;
    },
    get mySeat() {
      return s.mySeat;
    },
    pick(card: CardTypeId) {
      // Queue while disconnected: the server validates every pick, so a stale
      // or now-illegal resent pick is rejected with no state change.
      send({ t: "draftPick", card }, true);
    },
    pass() {
      send({ t: "draftPass" }, true);
    },
  };

  // The authoritative clock arrives as a snapshot (remaining ms, adjusted by
  // the server at send time); the running seat ticks down locally from receipt.
  // performance.now() at the last snapshot, so display = remaining - elapsed.
  let clockReceivedAt = 0;
  const [clockTick, setClockTick] = createSignal(0);
  let ticker: ReturnType<typeof setInterval> | undefined;

  const remoteClock: ChessClock = {
    get timeControl() {
      return { baseMs: s.clock?.baseMs ?? 0, incrementMs: s.clock?.incrementMs ?? 0 };
    },
    remaining(player: PlayerId): number {
      void clockTick(); // re-evaluate every local tick
      const snap = s.clock;
      if (!snap) return 0;
      const banked = snap.remaining[player]!;
      if (snap.running !== player) return Math.max(0, banked);
      return Math.max(0, banked - (performance.now() - clockReceivedAt));
    },
    running(player: PlayerId): boolean {
      return s.clock?.running === player;
    },
    dispose() {
      if (ticker) clearInterval(ticker);
      ticker = undefined;
    },
  };

  let ws: WebSocket | null = null;
  let closedByUs = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  // Messages to (re)send once the socket (re)opens: both lobby messages
  // (submitDeck, rematch) and in-game actions (action, draftPick, draftPass)
  // sent during a disconnect. The server is authoritative and validates every
  // message, so resending a stale/now-illegal one is rejected with no state
  // change — it can't corrupt the game. Flushed (and cleared) in ws.onopen.
  const outbox: ClientMessage[] = [];

  function connect(): void {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const auth = token === null ? "spectate=1" : `token=${encodeURIComponent(token)}`;
    ws = new WebSocket(`${proto}://${location.host}/api/rooms/${roomId}/ws?${auth}`);
    ws.onopen = () => {
      const pending = outbox.splice(0);
      for (const m of pending) ws!.send(JSON.stringify(m));
    };
    ws.onmessage = (e) => handle(JSON.parse(e.data as string) as ServerMessage);
    ws.onclose = () => {
      if (closedByUs) return;
      set("status", "disconnected");
      reconnectTimer = setTimeout(connect, 1500);
    };
  }

  function handle(msg: ServerMessage): void {
    switch (msg.t) {
      case "welcome":
        set({ mySeat: msg.mySeat, seats: msg.seats, status: msg.phase, mode: msg.mode, error: null });
        break;
      case "lobby":
        set({ seats: msg.seats, status: msg.phase, mode: msg.mode });
        break;
      case "draft":
        set("draft", reconcile(msg.draft as DraftState));
        set({ seats: msg.seats, status: msg.phase, mode: msg.mode, error: null });
        break;
      case "state":
        // reconcile the game subtree for fine-grained reactivity.
        set("game", reconcile(msg.state as GameState));
        set({ seats: msg.seats, status: msg.phase, error: null });
        if (msg.clock) {
          clockReceivedAt = performance.now();
          set("clock", msg.clock);
          if (!ticker && msg.clock.running !== null) {
            ticker = setInterval(() => setClockTick((t) => t + 1), 200);
          }
          if (msg.clock.running === null && ticker) {
            clearInterval(ticker);
            ticker = undefined;
          }
        }
        break;
      case "error":
        set("error", msg.message);
        break;
      case "rated":
        // A ranked game just settled: surface the change and refresh the account.
        set("rated", { delta: msg.delta, rating: msg.rating });
        void refreshAccount();
        break;
    }
  }

  function send(m: ClientMessage, queueIfClosed = false): boolean {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(m));
      return true;
    }
    if (queueIfClosed) {
      // Avoid double-queueing the same message (e.g. repeated clicks on the
      // same move while disconnected): drop an exact duplicate already queued.
      const serialized = JSON.stringify(m);
      if (!outbox.some((q) => JSON.stringify(q) === serialized)) outbox.push(m);
    }
    return false;
  }

  connect();

  return {
    get state() {
      return s.game as GameState;
    },
    get error() {
      return s.error;
    },
    get canUndo() {
      return false;
    },
    dispatch(action: Action): boolean {
      // Queue while disconnected so the action is (re)sent on reconnect rather
      // than dropped; the authoritative server validates it then.
      const sent = send({ t: "action", action }, true);
      if (!sent) set("error", "Reconnecting — your move will be sent when you're back online.");
      return sent;
    },
    undo() {
      /* no undo in online play */
    },
    clearError() {
      set("error", null);
    },
    status: () => s.status,
    seats: () => s.seats,
    mySeat: () => s.mySeat,
    mode: () => s.mode,
    submitDeck(name: string, build: DeckBuild) {
      send({ t: "submitDeck", name, build }, true);
    },
    setMode(mode: RoomMode) {
      send({ t: "setMode", mode }, true);
    },
    rematch() {
      send({ t: "rematch" }, true);
    },
    rated: () => s.rated,
    clock: () => (s.clock ? remoteClock : null),
    draftBackend: () => (s.draft ? draftBackend : null),
    close() {
      closedByUs = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      remoteClock.dispose();
      ws?.close();
    },
  };
}
